/* /professionals/:id — Public professional profile.
 *
 * SEO-friendly detail page. Premium engineering aesthetic — Navy + Gold,
 * not freelance marketplace. Includes services offered, cities served,
 * languages, optional portfolio image, and two CTAs:
 *   1. Request Consultation → InquiryModal
 *   2. WhatsApp Team Mera Naksha → routes via central number (so the
 *      professional's personal number is never exposed publicly)
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Briefcase,
  Languages,
  IndianRupee,
  CheckCircle2,
  Sparkles,
  Loader2,
  Inbox,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import {
  CATEGORY_BY_KEY,
  CategoryIcon,
  VerifiedBadge,
  FeaturedBadge,
  categoryHeadline,
} from "@/professionals/categories";
import BrandLogo from "@/components/BrandLogo";
import InquiryModal from "@/professionals/InquiryModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const ProfessionalProfile = () => {
  const { id } = useParams();
  const [pro, setPro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios
      .get(`${API}/professionals/${id}`)
      .then(({ data }) => {
        if (cancel) return;
        setPro(data);
        document.title = `${data.full_name} · ${CATEGORY_BY_KEY[data.category]?.label || "Professional"} · Mera Naksha`;
      })
      .catch((err) => {
        if (cancel) return;
        setError(err?.response?.status === 404 ? "We couldn't find this professional." : "Could not load this profile.");
      })
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [id]);

  const meta = pro ? CATEGORY_BY_KEY[pro.category] : null;

  return (
    <div className="min-h-screen bg-sand" data-testid="pro-profile-page">
      <header className="bg-navy text-white border-b-4 border-gold">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/professionals" data-testid="pro-back" className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> All professionals
          </Link>
          <div className="hidden sm:block">
            <Link to="/" className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm">
              <BrandLogo size="sm" asLink={false} withTagline lang="en" testId="pro-brand-logo" />
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {loading && (
          <div className="rounded-3xl bg-white border-2 border-border p-10 text-center text-muted-foreground inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading profile…
          </div>
        )}
        {error && (
          <div className="rounded-3xl bg-white border-2 border-dashed border-border p-10 text-center" data-testid="pro-not-found">
            <Inbox className="h-10 w-10 mx-auto text-navy/30" />
            <div className="mt-3 font-bold text-navy">{error}</div>
            <Link to="/professionals" className="mt-3 inline-block text-navy underline font-bold">
              Browse all professionals
            </Link>
          </div>
        )}

        {pro && (
          <>
            {/* Hero */}
            <section className="rounded-3xl overflow-hidden bg-white border-2 border-border shadow-sm">
              <div className="bg-navy-jaali text-white p-5 sm:p-7 relative">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
                <div className="flex items-start gap-4 sm:gap-6 flex-wrap">
                  <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-white text-navy font-extrabold text-2xl grid place-items-center border-2 border-gold/50 overflow-hidden">
                    {pro.profile_photo_url ? (
                      <img src={pro.profile_photo_url} alt={pro.full_name} className="h-full w-full object-cover" />
                    ) : (
                      pro.full_name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">{pro.full_name}</h1>
                      <VerifiedBadge />
                      {pro.featured && <FeaturedBadge />}
                    </div>
                    <div
                      className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: meta?.accent || "#0b2545", color: "#fff" }}
                    >
                      <CategoryIcon category={pro.category} className="h-3.5 w-3.5" />
                      {categoryHeadline(pro.category)}
                    </div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12.5px] text-white/85">
                      <div className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" /> {pro.city}, {pro.state}</div>
                      <div className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-gold" /> {pro.experience_years} years</div>
                      {pro.languages?.length > 0 && (
                        <div className="inline-flex items-center gap-1.5 col-span-2 sm:col-span-1"><Languages className="h-3.5 w-3.5 text-gold" /> {pro.languages.join(", ")}</div>
                      )}
                      {pro.consultation_fee != null && (
                        <div className="inline-flex items-center gap-1.5"><IndianRupee className="h-3.5 w-3.5 text-gold" /> {formatINR(pro.consultation_fee)} consultation</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-7 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setInquiryOpen(true)}
                  data-testid="pro-request-consultation"
                  className="flex-1 min-h-[52px] rounded-2xl bg-gold text-navy font-extrabold text-[14.5px] inline-flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)]"
                >
                  <Sparkles className="h-4 w-4" /> Request Consultation
                </button>
                <a
                  href={`https://wa.me/919717667115?text=${encodeURIComponent(`Hi Mera Naksha team, please connect me with ${pro.full_name} (Ref: ${pro.id}).`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pro-whatsapp"
                  className="flex-1 min-h-[52px] rounded-2xl bg-emerald-600 text-white font-extrabold text-[14.5px] inline-flex items-center justify-center gap-2 hover:bg-emerald-700"
                >
                  <FaWhatsapp className="h-4 w-4" /> WhatsApp Team Mera Naksha
                </a>
              </div>
            </section>

            {/* About */}
            <section className="rounded-3xl bg-white border-2 border-border p-5 sm:p-7" data-testid="pro-about">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-navy/70 font-extrabold">About</div>
              <h2 className="mt-1 text-lg font-extrabold text-navy">Customized Design Support for Your Dream Home</h2>
              <p className="mt-3 text-[14px] text-foreground leading-relaxed whitespace-pre-wrap">
                {pro.bio}
              </p>
            </section>

            {/* Services */}
            {meta?.services?.length > 0 && (
              <section className="rounded-3xl bg-white border-2 border-border p-5 sm:p-7" data-testid="pro-services">
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-navy/70 font-extrabold">Services offered</div>
                <h2 className="mt-1 text-lg font-extrabold text-navy">{meta.headline}</h2>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {meta.services.map((s) => (
                    <div
                      key={s}
                      className="flex items-start gap-2 px-3 py-2 rounded-xl bg-sand-alt border border-border text-[13px] font-semibold text-navy"
                    >
                      <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
                      {s}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Portfolio */}
            {(pro.portfolio_urls?.length > 0 || pro.portfolio_url) && (
              <section className="rounded-3xl bg-white border-2 border-border overflow-hidden" data-testid="pro-portfolio">
                <div className="p-5 sm:p-7">
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-navy/70 font-extrabold">Portfolio</div>
                  <h2 className="mt-1 text-lg font-extrabold text-navy">Recent work</h2>
                </div>
                <div className="border-t border-border bg-sand-alt p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {((pro.portfolio_urls && pro.portfolio_urls.length > 0)
                    ? pro.portfolio_urls
                    : pro.portfolio_url ? [pro.portfolio_url] : []
                  ).map((url, i) => (
                    <a
                      key={url + i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`pro-portfolio-item-${i}`}
                      className="block aspect-[4/3] rounded-2xl overflow-hidden bg-white border border-border shadow-sm group"
                    >
                      <img
                        src={url}
                        alt={`${pro.full_name} portfolio ${i + 1}`}
                        loading="lazy"
                        className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform"
                      />
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* Cities served */}
            {pro.service_areas?.length > 0 && (
              <section className="rounded-3xl bg-white border-2 border-border p-5 sm:p-7" data-testid="pro-service-areas">
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-navy/70 font-extrabold">Cities served</div>
                <h2 className="mt-1 text-lg font-extrabold text-navy">Available across</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {pro.service_areas.map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-navy text-gold text-[12.5px] font-bold border border-navy"
                    >
                      <MapPin className="h-3 w-3" /> {c}
                    </span>
                  ))}
                </div>
              </section>
            )}

            <section className="rounded-3xl bg-navy text-white p-5 sm:p-6 text-center">
              <div className="text-[10.5px] uppercase tracking-wider text-gold font-bold">Ready to start?</div>
              <h3 className="text-lg sm:text-xl font-extrabold tracking-tight mt-1">
                Connect with {pro.full_name} today
              </h3>
              <p className="text-[13.5px] text-white/80 mt-1 max-w-xl mx-auto">
                We'll route your requirement, schedule a consultation, and stay involved until your design is finalised.
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setInquiryOpen(true)}
                  className="min-h-[48px] px-6 rounded-2xl bg-gold text-navy font-extrabold text-[14px] hover:bg-gold-dark hover:text-white"
                  data-testid="pro-cta-bottom"
                >
                  Request Consultation
                </button>
              </div>
            </section>
          </>
        )}
      </main>

      <InquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} professional={pro} />
    </div>
  );
};

export default ProfessionalProfile;
